import {
  ErrorCodes,
  HostEvents,
  HostMethods,
  ModuleMethods,
  type HostMethodName,
  type ModuleManifest,
  type ModuleMethodName,
  type ModuleMethodParams,
  type ModuleMethodResult,
  meets,
  type Permission,
  type Requirement,
} from '@vp/shared-types';
import { RpcEndpoint, RpcError, type PortLike } from './rpc';

export type ModuleState = 'stopped' | 'starting' | 'running' | 'crashed' | 'isolated' | 'disabled' | 'incompatible' | 'error';

export interface HostEnv {
  createWorker(): Worker;
  /** Executes a validated, permitted module → host call. */
  handle(moduleId: string, method: HostMethodName, params: unknown): Promise<unknown>;
  onEvent(moduleId: string, method: string, params: unknown): void;
  onViolation(moduleId: string, reason: string, serious: boolean): void;
  onCrash(moduleId: string, error: string): void;
  onTiming?(moduleId: string, method: string, ms: number): void;
}

export const DEFAULT_TIMEOUTS: Partial<Record<ModuleMethodName, number>> = {
  activate: 8000,
  render: 3000,
  renderRaster: 5000,
  compute: 800,
  migrate: 5000,
  createDefaults: 2000,
  command: 15000,
  view: 5000,
  searchText: 2000,
  mcpTool: 15000,
  importFile: 60000,
  toolGesture: 15000,
  exportFile: 30000,
};

const RATE_WINDOW_MS = 1000;
const RATE_LIMIT = 2000; // host calls per second per module

/**
 * API 4.2: objects a module creates on the open canvas. One call carries at most CREATE_BATCH_MAX objects
 * and a module creates at most CREATE_WINDOW_MAX in CREATE_WINDOW_MS; a module that exceeds either is
 * isolated at once.
 */
export const CREATE_BATCH_MAX = 5000;
export const CREATE_WINDOW_MAX = 50_000;
export const CREATE_WINDOW_MS = 10_000;
const CREATING = new Set(['objects.create', 'objects.createTree']);
/** API 4.5: new canvases (`canvases.create`), a limit of their own: CANVAS_WINDOW_MAX in CANVAS_WINDOW_MS, else isolation at once. */
export const CANVAS_WINDOW_MAX = 100;
export const CANVAS_WINDOW_MS = 10_000;

/** Runs one module in its own Worker and mediates every message (§8.2, §9). */
export class ModuleHost {
  state: ModuleState = 'stopped';
  private worker: Worker | null = null;
  private rpc: RpcEndpoint | null = null;
  private callTimes: number[] = [];
  private created: { at: number; n: number }[] = [];
  private canvasTimes: number[] = [];
  readonly granted: Set<Permission>;

  constructor(
    readonly manifest: ModuleManifest,
    granted: Iterable<Permission>,
    private readonly env: HostEnv,
  ) {
    this.granted = new Set(granted);
  }

  get id(): string {
    return this.manifest.id;
  }

  get pending(): number {
    return (this.rpc?.pendingCount ?? 0) + (this.rpc?.inflightCount ?? 0);
  }

  async start(code: string, messages: Record<string, Record<string, string>>, locale: string, apiVersion: string): Promise<void> {
    this.stop();
    this.state = 'starting';
    const worker = this.env.createWorker();
    this.worker = worker;
    worker.onerror = (e) => {
      e.preventDefault?.();
      this.crash(e.message || 'worker error');
    };
    worker.onmessageerror = () => this.env.onViolation(this.id, 'unreadable message', false);
    const rpc = new RpcEndpoint(worker as unknown as PortLike, {
      onInvalid: (reason) => this.env.onViolation(this.id, reason, false),
      onEvent: (method, params) => this.onEvent(method, params),
    }, 'h');
    this.rpc = rpc;
    for (const [name, def] of Object.entries(HostMethods)) {
      rpc.handle(name, async (params) => {
        this.rateCheck();
        if (CREATING.has(name)) this.creationCheck(name, params);
        else if (name === 'canvases.create') this.canvasCheck();
        const parsed = def.params.safeParse(params);
        if (!parsed.success) {
          this.env.onViolation(this.id, `invalid params for ${name}: ${parsed.error.issues[0]?.path.join('.')} ${parsed.error.issues[0]?.message}`, false);
          throw new RpcError(ErrorCodes.invalidParams, `invalid params for ${name}`);
        }
        const perm = (def as { permission?: Requirement }).permission;
        if (perm && !meets(this.granted, perm)) {
          this.env.onViolation(this.id, `permission ${perm} not granted (${name})`, false);
          throw new RpcError(ErrorCodes.permissionDenied, `permission ${perm} is not granted`);
        }
        const t0 = performance.now();
        try {
          return await this.env.handle(this.id, name as HostMethodName, parsed.data);
        } finally {
          this.env.onTiming?.(this.id, name, performance.now() - t0);
        }
      });
    }
    try {
      await this.call('activate', {
        moduleId: this.id,
        apiVersion,
        locale,
        permissions: [...this.granted],
        code,
        messages,
      });
      if (this.state === 'starting') this.state = 'running';
    } catch (e) {
      if (this.state === 'starting') {
        this.state = 'error';
        this.stop('error');
      }
      throw e;
    }
  }

  private rateCheck(): void {
    const now = performance.now();
    this.callTimes.push(now);
    while (this.callTimes.length && now - this.callTimes[0] > RATE_WINDOW_MS) this.callTimes.shift();
    if (this.callTimes.length > RATE_LIMIT) {
      this.env.onViolation(this.id, 'call rate exceeded', false);
      throw new RpcError(ErrorCodes.forbidden, 'call rate exceeded');
    }
  }

  /** Count the objects a call is about to create (before its parameters are parsed); too many — isolation. */
  private creationCheck(name: string, params: unknown): void {
    const list = (params as { objects?: unknown } | null)?.objects;
    const n = Array.isArray(list) ? list.length : 0;
    if (!n) return;
    const now = performance.now();
    while (this.created.length && now - this.created[0].at > CREATE_WINDOW_MS) this.created.shift();
    const total = this.created.reduce((sum, c) => sum + c.n, n);
    const reason = n > CREATE_BATCH_MAX ? `${name}: ${n} objects in one call (limit ${CREATE_BATCH_MAX})` : total > CREATE_WINDOW_MAX ? `${name}: ${total} objects in ${CREATE_WINDOW_MS / 1000} s (limit ${CREATE_WINDOW_MAX})` : null;
    if (reason) {
      this.env.onViolation(this.id, reason, true);
      throw new RpcError(ErrorCodes.forbidden, 'object creation limit exceeded');
    }
    this.created.push({ at: now, n });
  }

  /** Count a new canvas; more than CANVAS_WINDOW_MAX in the window — isolation. */
  private canvasCheck(): void {
    const now = performance.now();
    while (this.canvasTimes.length && now - this.canvasTimes[0] > CANVAS_WINDOW_MS) this.canvasTimes.shift();
    if (this.canvasTimes.length >= CANVAS_WINDOW_MAX) {
      this.env.onViolation(this.id, `canvases.create: more than ${CANVAS_WINDOW_MAX} canvases in ${CANVAS_WINDOW_MS / 1000} s`, true);
      throw new RpcError(ErrorCodes.forbidden, 'canvas creation limit exceeded');
    }
    this.canvasTimes.push(now);
  }

  private onEvent(method: string, params: unknown): void {
    const schema = (HostEvents as Record<string, { safeParse(v: unknown): { success: boolean } }>)[method];
    if (!schema || !schema.safeParse(params).success) {
      this.env.onViolation(this.id, `invalid event ${method}`, false);
      return;
    }
    this.env.onEvent(this.id, method, params);
  }

  /** Call the module with validation of the result and a timeout. */
  async call<M extends ModuleMethodName>(method: M, params: ModuleMethodParams<M>, opts: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<ModuleMethodResult<M>> {
    if (!this.rpc || (this.state !== 'running' && !(method === 'activate' && this.state === 'starting'))) {
      throw new RpcError(ErrorCodes.forbidden, `module ${this.id} is not running`);
    }
    const t0 = performance.now();
    const raw = await this.rpc.call(method, params, { timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUTS[method] ?? 5000, signal: opts.signal });
    this.env.onTiming?.(this.id, method, performance.now() - t0);
    const res = ModuleMethods[method].result.safeParse(raw);
    if (!res.success) {
      this.env.onViolation(this.id, `invalid result of ${method}: ${res.error.issues[0]?.path.join('.')} ${res.error.issues[0]?.message}`, false);
      throw new RpcError(ErrorCodes.invalidResult, `invalid result of ${method}`);
    }
    if (method === 'renderRaster') {
      const b = (raw as { bitmap?: unknown }).bitmap;
      if (typeof ImageBitmap !== 'undefined' && !(b instanceof ImageBitmap)) {
        this.env.onViolation(this.id, 'renderRaster did not return an ImageBitmap', false);
        throw new RpcError(ErrorCodes.invalidResult, 'invalid bitmap');
      }
      return { bitmap: b } as ModuleMethodResult<M>;
    }
    return res.data as ModuleMethodResult<M>;
  }

  emit(method: string, params: unknown): void {
    if (this.state === 'running') this.rpc?.emit(method, params);
  }

  setGranted(perms: Iterable<Permission>): void {
    this.granted.clear();
    for (const p of perms) this.granted.add(p);
    this.emit('permissions.changed', { permissions: [...this.granted] });
  }

  private crash(message: string): void {
    if (this.state === 'isolated' || this.state === 'disabled') return;
    this.stop('crashed');
    this.env.onCrash(this.id, message);
  }

  stop(state: ModuleState = 'stopped'): void {
    this.rpc?.close();
    this.rpc = null;
    this.worker?.terminate();
    this.worker = null;
    this.state = state;
  }
}
